
import {defineComponent, PropType} from "vue";
import gsap from "gsap";

// ember — the night base carrying the accent orange (logo pairing: navy + orange)
export type GradientVariant = "azure" | "royal" | "deep" | "night" | "ember";

// drift   — rays wander gently, bloom pulses (default, all-purpose)
// breathe — rays hold still, bloom slowly swells (calmest — team tiles, small cards)
// sweep   — rays traverse the surface (statement heroes)
// aurora  — rays rotate and glide organically (portrait, dark moods)
export type GradientMotion = "drift" | "breathe" | "sweep" | "aurora";

export default defineComponent({
  name: "GradientBg",
  props: {
    // color scheme, all built from colors.sass tokens
    variant: {type: String as PropType<GradientVariant>, default: "azure"},
    motion: {type: String as PropType<GradientMotion>, default: "drift"},
    animated: {type: Boolean, default: true},
    // parallax depth: layers tilt towards the pointer (listens on the parent tile)
    depth: {type: Boolean, default: false}
  },
  data() {
    return {
      tiltTarget: null as HTMLElement | null,
      onTiltMove: null as ((e: PointerEvent) => void) | null,
      onTiltLeave: null as (() => void) | null
    }
  },
  mounted() {
    if(!this.depth) return;
    if(window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const el = this.$el as HTMLElement;
    const target = (el.parentElement ?? el) as HTMLElement;

    this.onTiltMove = (e: PointerEvent) => {
      const rect = target.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width - 0.5;
      const ny = (e.clientY - rect.top) / rect.height - 0.5;
      gsap.to(el, {
        "--gTiltX": (nx * 7).toFixed(2) + "deg",
        "--gTiltY": (ny * 7).toFixed(2) + "deg",
        duration: 0.6, ease: "power2.out", overwrite: "auto"
      });
    };
    this.onTiltLeave = () => {
      gsap.to(el, {
        "--gTiltX": "0deg",
        "--gTiltY": "0deg",
        duration: 1, ease: "power2.out", overwrite: "auto"
      });
    };

    target.addEventListener("pointermove", this.onTiltMove);
    target.addEventListener("pointerleave", this.onTiltLeave);
    this.tiltTarget = target;
  },
  beforeUnmount() {
    if(this.tiltTarget){
      if(this.onTiltMove) this.tiltTarget.removeEventListener("pointermove", this.onTiltMove);
      if(this.onTiltLeave) this.tiltTarget.removeEventListener("pointerleave", this.onTiltLeave);
    }
  }
});
